import React, { useState, useEffect } from 'react';
import { Typography, Table, Tag, Button, Empty, message, Row, Col, Progress, Card, Space, Spin } from 'antd';
import { PlusOutlined, TeamOutlined, CalendarOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PageContainer, StyledCard, EmptyContainer } from './styles/SubscriptionsPage.styles';
import apiService from '@/services/api.service';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { formatDate } from '@/utils/date';
import { API_ENDPOINTS } from '@/constants/api.constants';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

interface ExtendedLicenseInfo {
    hasExtendedLicense: boolean;
    extendedLicensePrice: number;
    canCreateTournament: boolean;
}

interface Subscription {
    id: number;
    clubId: number;
    subscriptionPlanId: number | null;
    status: 'active' | 'cancelled' | 'expired' | 'pending';
    startDate: string;
    endDate: string | null;
    price: number;
    autoRenew: boolean;
    usedTournaments: number;
    usedParticipants: number;
    maxTournamentsOverride?: number | null;
    maxParticipantsOverride?: number | null;
    paymentMethod: string | null;
    notes: string | null;
    createdAt: string;
    updatedAt: string;
    club?: {
        id: number;
        name: string;
        city: string;
    };
    subscriptionPlan?: {
        id: number;
        name: string;
        description: string | null;
        price: number;
        durationDays: number | null;
        maxTournaments: number | null;
        maxParticipants: number | null;
        features: string[] | null;
    };
}

const SubscriptionsPage: React.FC = () => {
    const navigate = useNavigate();
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [loading, setLoading] = useState(false);
    const [extendedLicenseInfo, setExtendedLicenseInfo] = useState<ExtendedLicenseInfo | null>(null);
    const [extendedLicenseLoading, setExtendedLicenseLoading] = useState(true);

    const fetchExtendedLicenseInfo = async () => {
        try {
            setExtendedLicenseLoading(true);
            const res = await apiService.get<ApiResponse<ExtendedLicenseInfo>>(
                API_ENDPOINTS.PAYMENTS.EXTENDED_LICENSE_INFO
            );
            const data = res.data?.data;
            if (data) setExtendedLicenseInfo(data);
        } catch (e) {
            logger.error('Error fetching extended license info:', e);
        } finally {
            setExtendedLicenseLoading(false);
        }
    };

    const fetchSubscriptions = async () => {
        try {
            setLoading(true);
            const response = await apiService.get<ApiResponse<Subscription[]>>(
                API_ENDPOINTS.SUBSCRIPTIONS.MY_CLUB
            );
            
            // Нормализуем данные подписок
            const subscriptionsData = Array.isArray(response.data?.data) ? response.data.data : [];
            
            // Обрабатываем каждую подписку: парсим features из JSON строки, если нужно
            const normalizedSubscriptions = subscriptionsData
                .filter((sub: any) => sub && typeof sub === 'object' && sub.id !== undefined && sub.id !== null)
                .map((sub: any) => {
                    try {
                        // Обрабатываем subscriptionPlan, если он есть
                        if (sub.subscriptionPlan && typeof sub.subscriptionPlan === 'object') {
                            // Если features - строка, парсим её
                            if (typeof sub.subscriptionPlan.features === 'string' && sub.subscriptionPlan.features.trim()) {
                                try {
                                    const parsed = JSON.parse(sub.subscriptionPlan.features);
                                    sub.subscriptionPlan.features = Array.isArray(parsed) ? parsed : null;
                                } catch (e) {
                                    logger.warn('Failed to parse subscription plan features:', e);
                                    sub.subscriptionPlan.features = null;
                                }
                            } else if (!Array.isArray(sub.subscriptionPlan.features) && sub.subscriptionPlan.features !== null) {
                                sub.subscriptionPlan.features = null;
                            }
                            
                            // Убеждаемся, что durationDays может быть null
                            if (sub.subscriptionPlan.durationDays === undefined) {
                                sub.subscriptionPlan.durationDays = null;
                            }
                            
                            // Убеждаемся, что maxTournaments может быть null
                            if (sub.subscriptionPlan.maxTournaments === undefined) {
                                sub.subscriptionPlan.maxTournaments = null;
                            }
                            
                            // Убеждаемся, что maxParticipants может быть null
                            if (sub.subscriptionPlan.maxParticipants === undefined) {
                                sub.subscriptionPlan.maxParticipants = null;
                            }
                            
                            // Убеждаемся, что все строковые поля корректны
                            if (!sub.subscriptionPlan.name || typeof sub.subscriptionPlan.name !== 'string') {
                                sub.subscriptionPlan.name = 'Не указан';
                            }
                        }
                        
                        // Убеждаемся, что обязательные поля присутствуют
                        const normalized: Subscription = {
                            id: Number(sub.id) || 0,
                            clubId: Number(sub.clubId) || 0,
                            subscriptionPlanId: sub.subscriptionPlanId !== undefined && sub.subscriptionPlanId !== null ? Number(sub.subscriptionPlanId) : null,
                            status: (['active', 'cancelled', 'expired', 'pending'].includes(sub.status) ? sub.status : 'pending') as Subscription['status'],
                            price: Number(sub.price) || 0,
                            startDate: sub.startDate ? String(sub.startDate) : new Date().toISOString(),
                            endDate: sub.endDate ? String(sub.endDate) : null,
                            autoRenew: Boolean(sub.autoRenew ?? false),
                            usedTournaments: Number(sub.usedTournaments) || 0,
                            usedParticipants: Number(sub.usedParticipants) || 0,
                            maxTournamentsOverride: sub.maxTournamentsOverride != null ? Number(sub.maxTournamentsOverride) : undefined,
                            maxParticipantsOverride: sub.maxParticipantsOverride != null ? Number(sub.maxParticipantsOverride) : undefined,
                            paymentMethod: sub.paymentMethod ? String(sub.paymentMethod) : null,
                            notes: sub.notes ? String(sub.notes) : null,
                            createdAt: sub.createdAt ? String(sub.createdAt) : new Date().toISOString(),
                            updatedAt: sub.updatedAt ? String(sub.updatedAt) : new Date().toISOString(),
                            club: sub.club && typeof sub.club === 'object' ? {
                                id: Number(sub.club.id) || 0,
                                name: String(sub.club.name || ''),
                                city: String(sub.club.city || '')
                            } : undefined,
                            subscriptionPlan: sub.subscriptionPlan && typeof sub.subscriptionPlan === 'object' ? {
                                id: Number(sub.subscriptionPlan.id) || 0,
                                name: String(sub.subscriptionPlan.name || 'Не указан'),
                                description: sub.subscriptionPlan.description ? String(sub.subscriptionPlan.description) : null,
                                price: Number(sub.subscriptionPlan.price) || 0,
                                durationDays: sub.subscriptionPlan.durationDays !== undefined && sub.subscriptionPlan.durationDays !== null ? Number(sub.subscriptionPlan.durationDays) : null,
                                maxTournaments: sub.subscriptionPlan.maxTournaments !== undefined && sub.subscriptionPlan.maxTournaments !== null ? Number(sub.subscriptionPlan.maxTournaments) : null,
                                maxParticipants: sub.subscriptionPlan.maxParticipants !== undefined && sub.subscriptionPlan.maxParticipants !== null ? Number(sub.subscriptionPlan.maxParticipants) : null,
                                features: Array.isArray(sub.subscriptionPlan.features) 
                                    ? sub.subscriptionPlan.features.map((f: any) => String(f || '')).filter(Boolean)
                                    : null
                            } : undefined
                        };
                        
                        return normalized;
                    } catch (e) {
                        logger.error('Error normalizing subscription:', e, sub);
                        return null;
                    }
                })
                .filter((sub): sub is Subscription => {
                    // Дополнительная проверка: подписка должна быть валидной
                    return sub !== null && 
                           sub !== undefined && 
                           typeof sub === 'object' && 
                           typeof sub.id === 'number' && 
                           sub.id > 0 && 
                           typeof sub.status === 'string';
                });
            
            setSubscriptions(normalizedSubscriptions);
            logger.info(`Loaded ${normalizedSubscriptions.length} subscriptions`);
        } catch (error: any) {
            logger.error('Error fetching subscriptions:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Ошибка при загрузке подписок';
            message.error(errorMessage);
            setSubscriptions([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubscriptions();
    }, []);

    useEffect(() => {
        fetchExtendedLicenseInfo();
    }, []);

    const handleSubscribe = () => {
        navigate('/subscribe');
    };

    const columns: ColumnsType<Subscription> = [
        {
            title: 'План подписки',
            key: 'subscriptionPlan',
            render: (_: unknown, record: Subscription) => {
                if (!record || !record.subscriptionPlan) {
                    return 'Не указан';
                }
                return record.subscriptionPlan.name || 'Не указан';
            },
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const statusConfig: Record<string, { color: string; text: string }> = {
                    active: { color: 'green', text: 'Активна' },
                    cancelled: { color: 'red', text: 'Отменена' },
                    expired: { color: 'orange', text: 'Истекла' },
                    pending: { color: 'blue', text: 'Ожидает' }
                };
                const config = statusConfig[status] || { color: 'default', text: status };
                return <Tag color={config.color}>{config.text}</Tag>;
            },
        },
        {
            title: 'Дата начала',
            dataIndex: 'startDate',
            key: 'startDate',
            render: (date: string) => date ? formatDate(date) : '-',
        },
        {
            title: 'Дата окончания',
            dataIndex: 'endDate',
            key: 'endDate',
            render: (date: string | null) => date ? formatDate(date) : 'Без ограничения',
        },
        {
            title: 'Участники',
            key: 'participants',
            render: (_: unknown, record: Subscription) => {
                const used = record.usedParticipants ?? 0;
                const max = record.subscriptionPlan?.maxParticipants;
                return max != null ? `${used} / ${max}` : `${used} / ∞`;
            },
        },
        {
            title: 'Цена',
            dataIndex: 'price',
            key: 'price',
            render: (price: number | null | undefined) => {
                const priceValue = price ?? 0;
                return `${parseFloat(priceValue.toString()).toFixed(2)} ₽`;
            },
        },
    ];

    // Находим все активные подписки
    const activeSubscriptions = subscriptions.filter((sub): sub is Subscription => 
        sub !== null && 
        sub !== undefined && 
        sub.status === 'active'
    );

    return (
        <PageContainer>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2}>Подписки клуба</Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleSubscribe}
                    disabled={false} // Разрешаем покупку нескольких подписок - лимиты суммируются
                >
                    {activeSubscriptions.length > 0 ? 'Оформить дополнительную подписку' : 'Оформить подписку'}
                </Button>
            </div>

            {/* Блок расширенной лицензии */}
            <StyledCard style={{ marginBottom: 24 }}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <SafetyCertificateOutlined style={{ fontSize: 22, color: '#1890ff' }} />
                        <Title level={4} style={{ margin: 0 }}>Расширенная лицензия</Title>
                        {extendedLicenseLoading ? (
                            <Spin size="small" />
                        ) : extendedLicenseInfo?.hasExtendedLicense ? (
                            <Tag color="success">Оформлена</Tag>
                        ) : (
                            <Tag color="default">Не оформлена</Tag>
                        )}
                    </div>
                    <Text type="secondary">
                        Даёт возможность создавать турниры от имени клуба. Без расширенной лицензии создание турниров недоступно.
                    </Text>
                    {!extendedLicenseLoading && extendedLicenseInfo && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                            {extendedLicenseInfo.hasExtendedLicense ? (
                                <Button type="link" onClick={() => navigate('/extended-license')} style={{ padding: 0 }}>
                                    Подробнее
                                </Button>
                            ) : (
                                <>
                                    {extendedLicenseInfo.extendedLicensePrice > 0 && (
                                        <Text strong>{extendedLicenseInfo.extendedLicensePrice} ₽</Text>
                                    )}
                                    <Button
                                        type="primary"
                                        onClick={() => navigate('/extended-license')}
                                    >
                                        {extendedLicenseInfo.extendedLicensePrice > 0
                                            ? 'Оформить расширенную лицензию'
                                            : 'Перейти к расширенной лицензии'}
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </Space>
            </StyledCard>

            {activeSubscriptions.length > 0 && (
                <StyledCard>
                    <div style={{ marginBottom: 24 }}>
                        <Title level={4} style={{ marginBottom: 4 }}>
                            {activeSubscriptions.length > 1 
                                ? `Активные подписки (${activeSubscriptions.length})` 
                                : 'Текущая активная подписка'}
                        </Title>
<Text type="secondary">Лимиты участников списываются с клуба организатора при проведении турниров</Text>
                    </div>

                    {(() => {
                        let totalMaxParticipants = 0;
                        let totalUsedParticipants = 0;
                        let hasUnlimitedParticipants = false;
                        activeSubscriptions.forEach(sub => {
                            const plan = sub.subscriptionPlan;
                            if (plan) {
                                const maxP = sub.maxParticipantsOverride ?? plan.maxParticipants;
                                const numP = typeof maxP === 'number' && !isNaN(maxP) ? maxP : null;
                                if (numP === null) hasUnlimitedParticipants = true;
                                else if (!hasUnlimitedParticipants) totalMaxParticipants += numP;
                                totalUsedParticipants += sub.usedParticipants ?? 0;
                            }
                        });
                        const safeMaxP = Number.isFinite(totalMaxParticipants) ? totalMaxParticipants : 0;
                        const showUnlimitedP = hasUnlimitedParticipants || !Number.isFinite(totalMaxParticipants);
                        const remainingParticipants = showUnlimitedP ? null : Math.max(0, safeMaxP - totalUsedParticipants);
                        return (
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                <Col xs={24}>
                                    <Card size="small" style={{ background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)', border: '1px solid #91d5ff' }}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text strong style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <TeamOutlined /> Участники
                                            </Text>
                                            <div>
                                                <Text>
                                                    Использовано: <strong>{totalUsedParticipants}</strong>
                                                    {showUnlimitedP ? ' / Неограниченно' : ` из ${safeMaxP}`}
                                                    {remainingParticipants !== null && (
                                                        <> · Осталось: <strong>{remainingParticipants}</strong></>
                                                    )}
                                                </Text>
                                                {!showUnlimitedP && safeMaxP > 0 && (
                                                    <Progress
                                                        percent={Math.min(100, Math.round((totalUsedParticipants / safeMaxP) * 100))}
                                                        size="small"
                                                        showInfo={false}
                                                        strokeColor="#1890ff"
                                                        style={{ marginTop: 6 }}
                                                    />
                                                )}
                                            </div>
                                        </Space>
                                    </Card>
                                </Col>
                            </Row>
                        );
                    })()}

                    {activeSubscriptions.map((subscription, index) => {
                        const plan = subscription.subscriptionPlan;
                        const maxP = subscription.maxParticipantsOverride ?? plan?.maxParticipants ?? null;
                        const usedP = subscription.usedParticipants ?? 0;
                        const percentP = maxP != null && maxP > 0 ? Math.min(100, (usedP / maxP) * 100) : 0;
                        return (
                            <Card
                                key={subscription.id || index}
                                size="small"
                                style={{
                                    marginBottom: index < activeSubscriptions.length - 1 ? 16 : 0,
                                    borderLeft: '4px solid #1890ff',
                                    borderRadius: 8
                                }}
                                title={activeSubscriptions.length > 1 ? `Подписка #${index + 1}: ${plan?.name || 'План'}` : (plan?.name || 'План подписки')}
                                extra={subscription.endDate ? <Text type="secondary"><CalendarOutlined /> до {formatDate(subscription.endDate)}</Text> : null}
                            >
                                <Row gutter={[16, 12]}>
                                    <Col span={24}>
                                        <Text type="secondary">Дата начала: {formatDate(subscription.startDate)}</Text>
                                        {subscription.price != null && subscription.price > 0 && (
                                            <> · <Text type="secondary">{parseFloat(String(subscription.price)).toFixed(0)} ₽</Text></>
                                        )}
                                    </Col>
                                    <Col span={24}>
                                        <div style={{ marginBottom: 4 }}>
                                            <Text strong>Участники:</Text>{' '}
                                            {usedP}{maxP != null ? ` / ${maxP}` : ' / ∞'}
                                        </div>
                                        {maxP != null && maxP > 0 && (
                                            <Progress percent={Math.round(percentP)} size="small" strokeColor="#1890ff" />
                                        )}
                                    </Col>
                                    {plan?.features && Array.isArray(plan.features) && plan.features.length > 0 && (
                                        <Col span={24}>
                                            <Text type="secondary">Возможности: </Text>
                                            {plan.features.slice(0, 3).map((f: string, i: number) => (
                                                <Tag key={i} style={{ marginTop: 4 }}>{String(f)}</Tag>
                                            ))}
                                            {plan.features.length > 3 && <Tag>+{plan.features.length - 3}</Tag>}
                                        </Col>
                                    )}
                                </Row>
                            </Card>
                        );
                    })}
                </StyledCard>
            )}

            {subscriptions.length === 0 ? (
                <StyledCard>
                    <EmptyContainer>
                        <Empty
                            description="У вашего клуба пока нет подписок"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                        <Button
                            type="primary"
                            style={{ marginTop: 16 }}
                            onClick={handleSubscribe}
                        >
                            Оформить подписку
                        </Button>
                    </EmptyContainer>
                </StyledCard>
            ) : subscriptions.length > 0 ? (
                <StyledCard>
                    <Title level={4} style={{ marginBottom: 16 }}>История подписок</Title>
                    <Table
                        columns={columns}
                        dataSource={subscriptions}
                        rowKey={(record) => record.id?.toString() || String(Math.random())}
                        loading={loading}
                        pagination={{
                            showSizeChanger: true,
                            showTotal: (total) => `Всего ${total} подписок`,
                        }}
                    />
                </StyledCard>
            ) : null}
        </PageContainer>
    );
};

export default SubscriptionsPage;
